// The `donation` wire shape — one gift, one body, whichever door read it.
//
// Consumed by `GET /v1/donations`, `GET /v1/donations/received`, the create and
// cancel/decline responses, and the MCP `donation` verb's every action.
//
// ─── WHY THIS ONE EXISTS ─────────────────────────────────────────────────────
//
// The two copies it replaces are the clearest instance in the tree of the defect
// plan 101 slice 05 is named for: `toView` (`apps/api/src/routes/donations.ts`)
// and `maskDonation` (`packages/mcp/src/army/donation.ts`) emitted the same
// fifteen keys from the same `DonationView`, and BOTH files carried a comment
// ASSERTING they were byte-identical — "deliberately byte-identical to the
// verb's `maskDonation`" on one side, "one gift has ONE wire shape whichever
// door read it" on the other. Nothing mechanical held either sentence up. A
// comment asserting a guarantee the code does not provide is the house's
// "when the same bug comes back, the design is the bug" shape, so the sentences
// are deleted along with the copies: this module IS the guarantee now, and
// `donation.test.ts` is what fails when it stops being true.
//
// ─── WHAT CHANGED, AND WHAT DID NOT ──────────────────────────────────────────
//
// No key is added, removed, renamed or re-typed. The two copies agreed on every
// name, every nullability and every optionality; they differed only in the ORDER
// they emitted the four conditional fields (REST appended them after
// `revoked_iso`, MCP interleaved the first three after `target_repo_id`). JSON
// object order is not a contract to any parser, and neither door asserted it —
// but two orders is still two answers, so this module fixes one and both doors
// now emit it.
//
// ─── ABSENT, NEVER NULL ──────────────────────────────────────────────────────
//
// Four fields are `.optional()` rather than `.nullable()`, and the distinction is
// the published contract rather than an accident of the projection:
//
// - `target_repo_full_name` — a `pool` gift pins nothing, and a `project` gift
//   whose repo row no longer resolves has no name we can stand behind. A client
//   renders the unnamed row; it never renders a fabricated one.
// - `donor_login` — carried on the RECIPIENT's read only. A donor already knows
//   who they are, so echoing it on their own list would be a field with no
//   reader. Login only: the donor's email is projected nowhere in this chain.
// - `allowed_models` — the picker's VOCABULARY, never the authorization (the
//   port re-checks every pick and the pool resolver re-applies the ceiling at
//   seal time). Absent, never `[]`, when the gift carries no resolvable key: an
//   empty list reads as "nothing is permitted" rather than "there is nothing to
//   pick".
// - `key_spend_cap_usd` — an `llm` gift's monthly cap. Undefined on
//   money/compute, where there is no key to cap.

import { z } from 'zod';
import { defineResource } from './resource';

/**
 * The camelCase shape the projection reads.
 *
 * Declared structurally rather than imported: the entity is `DonationView` in
 * `@developerz/mcp`, and `@developerz/domain` imports nothing internal — it is
 * the one package both `apps/api` and `apps/dashboard` depend on, which is the
 * whole reason the wire layer lives here. `DigestEntity` has the same posture
 * and the same reason.
 */
export type DonationEntity = {
  id: string;
  /** `money` | `compute` | `llm`. */
  kind: string;
  /** `project` | `pool`. */
  targetKind: string;
  targetAccountId: string | null;
  targetRepoId: string | null;
  /** Derived at the port from `repositories.full_name` — a label, never state. */
  targetRepoFullName?: string;
  /** The giver's GitHub login. Recipient's read only; never an email. */
  donorLogin?: string;
  /** The resolved donor ceiling on an `llm` gift. `['*']` = any model. */
  allowedModels?: readonly string[];
  runnerId: string | null;
  byokKeyId: string | null;
  chosenModel: string | null;
  active: boolean;
  createdAt: Date;
  revokedAt: Date | null;
  keySpendCapUsd?: number;
};

/** The published body. Field names, nullability and optionality are the existing contract. */
export const DonationWireSchema = z.object({
  id: z.string(),
  kind: z.string(),
  target_kind: z.string(),
  target_account_id: z.string().nullable(),
  target_repo_id: z.string().nullable(),
  target_repo_full_name: z.string().optional(),
  donor_login: z.string().optional(),
  allowed_models: z.array(z.string()).readonly().optional(),
  runner_id: z.string().nullable(),
  byok_key_id: z.string().nullable(),
  chosen_model: z.string().nullable(),
  active: z.boolean(),
  created_iso: z.string(),
  revoked_iso: z.string().nullable(),
  key_spend_cap_usd: z.number().optional(),
});

/** What a client should type against for a donation body. */
export type DonationWire = z.infer<typeof DonationWireSchema>;

export const DonationResource = defineResource<DonationEntity, typeof DonationWireSchema>({
  schema: DonationWireSchema,
  serialize: (d) => ({
    id: d.id,
    kind: d.kind,
    target_kind: d.targetKind,
    target_account_id: d.targetAccountId,
    target_repo_id: d.targetRepoId,
    // The conditional four. Spread-when-present rather than `?? null`: see the
    // header — absence and null mean different things to every one of them.
    ...(d.targetRepoFullName === undefined ? {} : { target_repo_full_name: d.targetRepoFullName }),
    ...(d.donorLogin === undefined ? {} : { donor_login: d.donorLogin }),
    ...(d.allowedModels === undefined ? {} : { allowed_models: d.allowedModels }),
    runner_id: d.runnerId,
    byok_key_id: d.byokKeyId,
    chosen_model: d.chosenModel,
    active: d.active,
    created_iso: d.createdAt.toISOString(),
    revoked_iso: d.revokedAt === null ? null : d.revokedAt.toISOString(),
    ...(d.keySpendCapUsd === undefined ? {} : { key_spend_cap_usd: d.keySpendCapUsd }),
  }),
});
